import { useStrictLocalization } from "@faire/web/common/localization";
import { capitalizeEveryWord } from "@faire/web/common/string";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { WhenVisibilityIsImminent } from "@faire/web/ui/VisibilitySensor/WhenVisibilityIsImminent";
import * as React from "react";
import { Suspense } from "react";

import { Breadcrumb } from "@faire/retailer/components/Breadcrumb";
import { FaireHelmet } from "@faire/retailer/components/Helmet";
import { RetrievalQueries } from "@faire/retailer/features/RetrievalQueries/RetrievalQueries";
import { ExplorePageFeaturedBrandsCarousel } from "@faire/retailer/views/explore-page/__internal__/ExplorePageFeaturedBrandsCarousel";
import { LazyExplorePageLayout } from "@faire/retailer/views/explore-page/__internal__/ExplorePageLayout";
import { ExplorePageLayoutSkeleton } from "@faire/retailer/views/explore-page/__internal__/ExplorePageLayout/ExplorePageLayoutSkeleton";
import { ExploreProductsResultsList } from "@faire/retailer/views/explore-page/__internal__/ExploreProductsResultsList";
import { OuterResultsContentWrapper } from "@faire/retailer/views/explore-page/__internal__/ExploreStyles";
import { useExplorePageProductsQuery } from "@faire/retailer/views/explore-page/__internal__/useExplorePageProductsQuery";
import { useExplorePageSearchTerm } from "@faire/retailer/views/explore-page/__internal__/useExplorePageSearchTerm";

const ExploreBreadcrumb: React.FC = () => {
  const { productSearchString } = useExplorePageSearchTerm();
  return (
    <>
      <Spacer size="2x" />
      <Breadcrumb
        options={[
          {
            label: `"${productSearchString}"`,
          },
        ]}
        verticalPadding="0"
        horizontalPadding="0"
      />
      <Spacer size="1x" />
    </>
  );
};

export const ExplorePageContent: React.FC<{
  loadTimingRef?: React.Ref<HTMLDivElement>;
}> = ({ loadTimingRef }) => {
  const { productSearchString } = useExplorePageSearchTerm();
  const { strictLocalize } = useStrictLocalization();
  const { isSearchPending, requestId, allRetrievalQueries } =
    useExplorePageProductsQuery();
  const helmetData = () => {
    let queryString = productSearchString.replace(/Wholesale/gi, "");
    queryString = capitalizeEveryWord(queryString);
    const title = strictLocalize(
      {
        defaultMessage:
          "Purchase Wholesale {queryFullyTranslatedSentence}. Buy now, pay 60 days later—interest free.",
        description: {
          text: "Meta title for explore page, {queryFullyTranslatedSentence} being the searched term",
        },
      },
      { queryFullyTranslatedSentence: queryString }
    );
    const description = strictLocalize(
      {
        defaultMessage:
          "Shop wholesale {queryFullyTranslatedSentence} and all the latest products for your store. Stock up on brands your customers will love, with net 60 term and free returns.",
        description: {
          text: "Meta description for explore page, {queryFullyTranslatedSentence} being the searched term",
        },
      },
      { queryFullyTranslatedSentence: queryString }
    );
    const header = strictLocalize(
      {
        defaultMessage: "Wholesale {queryFullyTranslatedSentence}",
        description: {
          text: "Page header for explore page, {queryFullyTranslatedSentence} being the searched term",
        },
      },
      { queryFullyTranslatedSentence: queryString }
    );

    return { title, description, header };
  };

  const { title, description, header } = helmetData();
  return (
    <OuterResultsContentWrapper>
      <ExploreBreadcrumb />
      <FaireHelmet title={title} description={description} />
      <Typography variant="displaySSerifRegular" as="h1">
        {header}
      </Typography>
      <Spacer height="5x" />
      <RetrievalQueries
        allRetrievalQueries={allRetrievalQueries}
        requestId={requestId}
      />
      <ExploreProductsResultsList
        title={header}
        loadTimingRef={loadTimingRef}
      />
      <WhenVisibilityIsImminent>
        <Suspense fallback={<ExplorePageLayoutSkeleton />}>
          <LazyExplorePageLayout />
        </Suspense>
      </WhenVisibilityIsImminent>

      {!isSearchPending ? <ExplorePageFeaturedBrandsCarousel /> : null}
    </OuterResultsContentWrapper>
  );
};
